import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { filterItemsToOptions, generateOptionItems } from 'components/OfferList/OfferListFilter/OfferListFilterUtils'
import useToggle from 'hooks/useToggle'
import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ComponentProps } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Modal from 'LuxKit/Modal/Modal'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import LayoutContainer from 'components/Common/LayoutContainer'
import type { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'

interface Props {
  mapOverlay?: boolean
  availableFilters: Array<App.FilterItem> | undefined
  label: string
  selectedFilters: Array<string>
  search: string
  searchQueryKey: string
  place?: App.Place
  onFilterChange: FilterControlChangeHandler
  filterChipSize?: ComponentProps<typeof FilterChip>['size']
}

function InclusionsDropdown(props: Props) {
  const { availableFilters, selectedFilters, label, search, searchQueryKey, onFilterChange, filterChipSize = 'medium', mapOverlay = false } = props
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const [isDropdownOpen, toggleDropDown, , closeDropdown] = useToggle()
  const [localSelectedFilters, setLocalSelectedFilters] = useState<Set<string>>(new Set(selectedFilters))
  const filterChipRef = useRef<HTMLButtonElement>(null)
  const inclusionItems = useMemo(() => {
    // Hide counts because one hotel can appear more than once in search results.
    return generateOptionItems(filterItemsToOptions(availableFilters ?? []), new Set(localSelectedFilters), { hideCounts: mapOverlay })
  }, [availableFilters, localSelectedFilters, mapOverlay])

  const onFilterTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSelectedFilters((prev) => {
      const newSet = new Set(prev)
      if (e.target.checked) {
        newSet.add(e.target.value)
      } else {
        newSet.delete(e.target.value)
      }
      return newSet
    })
  }, [])

  const onApply = useCallback(() => {
    onFilterChange({ search: setSearchParamValue(search, searchQueryKey, Array.from(localSelectedFilters).join(',')) })
    closeDropdown()
  }, [closeDropdown, localSelectedFilters, onFilterChange, search, searchQueryKey])

  const onClose = useCallback(() => {
    onApply()
    closeDropdown()
  }, [closeDropdown, onApply])

  const onReset = useCallback(() => {
    setLocalSelectedFilters(new Set())
  }, [])

  useEffect(() => {
    setLocalSelectedFilters(new Set(selectedFilters))
  }, [selectedFilters])

  const hasAvailableFilters = (availableFilters?.length ?? 0) > 0

  const resetText = intl.formatMessage({
    id: 'RAHy2iev_xzNi9PbHiRO',
    defaultMessage: 'Reset',
    description: 'Button text to reset inclusions filter in the search or listing filters panel',
  })

  const applyText = intl.formatMessage({
    id: 'rooDZXgr7NIYL_4PKy8C',
    defaultMessage: 'Apply',
    description: 'Button text to apply inclusions filter in the search or listing filters panel',
  })

  const noAvailableFiltersText = intl.formatMessage({
    id: '35rJ0F53OKmeRrPwsl7g',
    defaultMessage: 'No available filters',
    description: 'Message shown when no inclusion filters are available',
  })

  const resetAllText = intl.formatMessage({
    id: 'w2g9RLcGZU97OgZAHw8U',
    defaultMessage: 'Reset all',
    description: 'Button text to reset all selected inclusions in the inclusions list',
  })

  const formatLabelWithCount = useCallback(
    (baseLabel: string) => {
      return localSelectedFilters.size ?
          intl.formatMessage(
            {
              id: '5HtI1Lmi-09QyeiNtM70',
              defaultMessage: '{label} ({count})',
              description:
              'Filter label showing the base text followed by the selected inclusion count in hotel search',
            },
            {
              label: baseLabel,
              count: localSelectedFilters.size,
            },
          ) :
        baseLabel
    },
    [intl, localSelectedFilters.size],
  )

  const resetLabel = formatLabelWithCount(resetAllText)
  const chipLabel = formatLabelWithCount(label)

  const filterContent = <>
    {!hasAvailableFilters && <BodyText variant="medium">{noAvailableFiltersText}</BodyText>}
    {hasAvailableFilters && <FilterPanelCheckboxGroup
      name="inclusions"
      items={inclusionItems}
      onChange={onFilterTypeChange}
      maxItems={5}
      allowShowMore={false}
    />}
  </>

  return (
    <div>
      <FilterChip aria-label={mapOverlay ? chipLabel : undefined} data-map-filter-trigger={mapOverlay || undefined} selected={!!localSelectedFilters.size} ref={filterChipRef} size={filterChipSize} onClick={toggleDropDown} endIcon={isDropdownOpen ? <LineAngleUpIcon /> : <LineAngleDownIcon />}>
        {chipLabel}
      </FilterChip>
      <CSSBreakpoint min="tablet">
        <DropdownList
          data-testid={mapOverlay ? 'hotel-map-filter-dropdown' : undefined}
          data-map-filter-dropdown={mapOverlay || undefined}
          autoHeight={mapOverlay || undefined}
          title={mapOverlay ? label : undefined}
          size="M"
          triggerRef={filterChipRef}
          anchorRef={filterChipRef}
          open={isDropdownOpen}
          placement="bottom-start"
          onClose={onClose}
          secondaryActionProps={{
            kind: 'tertiary',
            children: resetText,
            onClick: onReset,
          }}
          primaryActionProps={{
            children: applyText,
            onClick: onApply,
          }}
        >
          <LayoutContainer>
            {mapOverlay ? filterContent : (
              <FilterPanelFilterGroup allowToggle={false} showDropdown={false} title={label} showBorder={false}>
                {filterContent}
              </FilterPanelFilterGroup>
            )}
          </LayoutContainer>
        </DropdownList>
      </CSSBreakpoint>
      <CSSBreakpoint max="mobile">
        <Modal
          title={label}
          isOpen={isDropdownOpen && isMobile}
          onClose={closeDropdown}
          primaryActionText={applyText}
          onPrimaryActionClick={onClose}
          secondaryActionText={resetLabel}
          onSecondaryActionClick={onReset}
        >
          {filterContent}
        </Modal>
      </CSSBreakpoint>
    </div>
  )
}

export default InclusionsDropdown
